import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import client from '../api/client';
import { getVehicles, updateVehicle, deleteVehicle, getVehicleTripLocation, approveVehicleModeration, rejectVehicleModeration, requestChangesVehicleModeration } from '../api/admin';

const MODERATION_BADGE = {
  draft:              { bg: '#f1f5f9', color: '#475569', icon: '📝', text: 'Draft' },
  submitted:          { bg: '#fef9c3', color: '#854d0e', icon: '⏳', text: 'Submitted' },
  changes_requested:  { bg: '#ffedd5', color: '#9a3412', icon: '✏️', text: 'Changes requested' },
  approved:           { bg: '#dcfce7', color: '#166534', icon: '✅', text: 'Approved' },
  rejected:           { bg: '#fee2e2', color: '#991b1b', icon: '❌', text: 'Rejected' },
};

const STATUSES = [
  { value: 'all',          label: 'All Vehicles',   icon: '◉' },
  { value: 'listed',       label: 'Listed & Live',  icon: '✅' },
  { value: 'unavailable',  label: 'Temp Unavailable', icon: '⚠️' },
  { value: 'hidden',       label: 'Unlisted/Hidden', icon: '👁' },
];

const CATEGORY_OPTIONS = ['economy','sedan','suv','luxury','van','truck','scooter','bike','transit'];
const PROVIDER_GROUP_OPTIONS = ['p2p','carshare','scooter','ride','public_transit'];

const statusBadge = (v) => {
  if (!v.is_listed_publicly) return { bg: '#f1f5f9', color: '#475569', icon: '👁', text: 'Unlisted' };
  if (!v.is_available)      return { bg: '#fffbeb', color: '#92400e', icon: '⚠️', text: 'Unavailable' };
  return { bg: '#ecfdf5', color: '#065f46', icon: '✅', text: 'Live on map' };
};

export default function Vehicles() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('');
  const [provider_group, setProviderGroup] = useState('');
  const [owner_id, setOwnerId] = useState('');
  const [editing, setEditing] = useState(null); // vehicle id being edited
  const [locationModal, setLocationModal] = useState(null); // { vehicleId, vehicleName, location }
  const [locLoading, setLocLoading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-vehicles', page, limit, search, status, category, provider_group, owner_id],
    queryFn: () => getVehicles({
      page, limit,
      search: search.trim() || undefined,
      status: status === 'all' ? undefined : status,
      category: category || undefined,
      provider_group: provider_group || undefined,
      owner_id: owner_id.trim() || undefined,
    }).then((r) => r.data),
    staleTime: 15_000,
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateVehicle(id, data).then((r) => r.data),
    onSuccess: () => { toast.success('Vehicle updated'); qc.invalidateQueries({ queryKey: ['admin-vehicles'] }); setEditing(null); },
    onError: (err) => {
      const d = err.response?.data || {};
      toast.error(
        (t) => (
          <div style={{ lineHeight: 1.4, maxWidth: 480, whiteSpace: 'pre-wrap' }}>
            <b>{d.error || err.message || 'Failed to update'}</b>
            {d.db_message ? <><br/><span style={{ color: '#7c2d12' }}>DB: {d.db_message}</span></> : null}
            {d.hint ? <><br/><span style={{ color: '#166534' }}>💡 {d.hint}</span></> : null}
            {d.allowed_fields ? <><br/><small>Allowed fields: {d.allowed_fields.join(', ')}</small></> : null}
          </div>
        ),
        { duration: 8000 }
      );
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id) => deleteVehicle(id).then((r) => r.data),
    onSuccess: (resp) => { toast.success(resp.message || 'Vehicle deleted'); qc.invalidateQueries({ queryKey: ['admin-vehicles'] }); },
    onError: (err) => {
      const d = err.response?.data || {};
      toast.error(
        (t) => (
          <div style={{ lineHeight: 1.45, maxWidth: 480, whiteSpace: 'pre-wrap' }}>
            <b>{d.error || err.message || 'Delete failed'}</b>
            {d.db_message ? <><br/><span style={{ color: '#7c2d12' }}>DB: {d.db_message}</span></> : null}
            {d.hint ? <><br/><span style={{ color: '#166534' }}>💡 {d.hint}</span></> : null}
          </div>
        ),
        { duration: 9000 }
      );
    },
  });

  // Vehicle Owner Onboarding v2.1 — per-vehicle moderation (separate from the
  // owner/company application approval in Applications.js).
  const moderationMut = useMutation({
    mutationFn: ({ id, action, reason }) => {
      if (action === 'approve') return approveVehicleModeration(id);
      if (action === 'reject') return rejectVehicleModeration(id, reason);
      return requestChangesVehicleModeration(id, reason);
    },
    onSuccess: (r) => { toast.success(r.data?.message || 'Vehicle moderation updated'); qc.invalidateQueries({ queryKey: ['admin-vehicles'] }); },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to update moderation status'),
  });

  const rows = data?.vehicles || [];
  const pagination = data?.pagination || {};
  const filters = data?.filters_applied || {};

  function saveEdit(v, form) {
    const numeric = ['year','seating_capacity','comfort_score','co2_grams_per_km',
      'price_per_day','price_per_hour','price_per_km','service_fee','minimum_rental_hours',
      'last_known_fuel_percent','last_known_odometer_km',
      'free_km_per_day','extra_km_price','max_km_per_booking'];
    const booleans = ['is_available','is_listed_publicly','is_listed_in_search','fleet_approval_required'];
    const payload = {};
    for (const k of Object.keys(form)) {
      let val = form[k];
      if (numeric.includes(k)) {
        if (val === '' || val === null || val === undefined) continue;
        val = Number(val);
      }
      if (booleans.includes(k)) {
        val = val === true || val === 'true' || val === '1' || val === 1;
      }
      if (k === 'taxes_declared' && val === '') val = null;
      if (k === 'currency_code' && !val) continue;
      if (k === 'reason' && !val) continue;
      payload[k] = val;
    }
    updateMut.mutate({ id: v.id, data: payload });
  }

  if (error) {
    const d = error.response?.data || {};
    return (
      <div style={{ padding: 24 }}>
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, color: '#b91c1c', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>Failed to load vehicles</div>
          <div>{d.error || error.message || 'Unknown error'}</div>
          {d.db_message && <div style={{ marginTop: 6 }}>🛢️ {d.db_message}</div>}
          {d.hint && <div style={{ marginTop: 6 }}>💡 {d.hint}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>🚗 Vehicles Moderation</h2>
          <div style={{ color: '#64748b', fontSize: 13.5, marginTop: 4 }}>
            {pagination.total ? `${pagination.total} total vehicles` : 'Search to view vehicles'}
            {Object.values(filters || {}).some(Boolean) && <span> · Filters active</span>}
          </div>
        </div>
      </div>

      {/* Filter status chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => { setStatus(s.value); setPage(1); }}
            style={{
              padding: '7px 14px',
              borderRadius: 999,
              fontWeight: 600,
              fontSize: 13,
              border: status === s.value ? '1px solid #0f766e' : '1px solid #e2e8f0',
              background: status === s.value ? '#0f766e' : '#fff',
              color: status === s.value ? '#fff' : '#0f172a',
              cursor: 'pointer',
            }}
          >
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 18 }}>
        <input placeholder="Search make, model, plate, owner, city…"
               value={search} onChange={(e) => setSearch(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') setPage(1); }}
               style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14 }}/>
        <input placeholder="Owner user_id (exact)"
               value={owner_id} onChange={(e) => setOwnerId(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') setPage(1); }}
               style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14 }}/>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}>
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={provider_group} onChange={(e) => { setProviderGroup(e.target.value); setPage(1); }}
                style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}>
          <option value="">All provider groups</option>
          {PROVIDER_GROUP_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={() => setPage(1)}
          style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid #0f766e', background: '#fff', color: '#0f766e', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
        >
          🔍 Apply Filters
        </button>
        <button
          onClick={() => { setSearch(''); setOwnerId(''); setCategory(''); setProviderGroup(''); setStatus('all'); setPage(1); }}
          style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: 14, cursor: 'pointer' }}
        >
          Reset
        </button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Vehicle', 'Owner', 'Location', 'Price', 'Status', 'Moderation', 'Actions'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 700, color: '#334155', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} style={{ padding: 24, color: '#64748b', textAlign: 'center' }}>⏳ Loading vehicles…</td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 40, color: '#64748b', textAlign: 'center' }}>No vehicles match these filters.</td></tr>
              )}
              {rows.map((v) => (
                <VehicleRow
                  key={v.id}
                  v={v}
                  editing={editing}
                  setEditing={setEditing}
                  saveEdit={saveEdit}
                  updateMutLoading={updateMut.isLoading}
                  deleteMut={deleteMut}
                  moderationMut={moderationMut}
                  onViewLocation={async () => {
                    setLocLoading(true);
                    try {
                      const { data } = await getVehicleTripLocation(v.id);
                      setLocationModal({
                        vehicleId: v.id,
                        vehicleName: `${v.year ? `${v.year} ` : ''}${v.make} ${v.model} (${v.license_plate || '—'})`,
                        location: data?.location || null,
                      });
                    } catch (err) {
                      const d = err.response?.data || {};
                      toast.error(d.error || 'Could not load trip location');
                    } finally {
                      setLocLoading(false);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>

        {pagination.total > pagination.limit && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', fontSize: 13 }}>
            <div style={{ color: '#475569' }}>
              Showing page <b>{pagination.page}</b> of <b>{pagination.pages || 1}</b> · <b>{pagination.total}</b> total vehicles
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={pagination.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                      style={pagerBtn(pagination.page <= 1)}>← Prev</button>
              <button disabled={pagination.page >= (pagination.pages || 1)} onClick={() => setPage((p) => p + 1)}
                      style={pagerBtn(pagination.page >= (pagination.pages || 1))}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {locationModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24,
        }} onClick={() => setLocationModal(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 20px 40px rgba(15,23,42,.2)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18 }}>📍 Shared trip location</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{locationModal.vehicleName}</p>
            {locLoading ? (
              <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>
            ) : locationModal.location ? (
              <>
                <div style={{ fontSize: 14, marginBottom: 8 }}>
                  <b>Latitude:</b> {Number(locationModal.location.lat || 0).toFixed(6)}
                </div>
                <div style={{ fontSize: 14, marginBottom: 8 }}>
                  <b>Longitude:</b> {Number(locationModal.location.lng || 0).toFixed(6)}
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                  Shared at: {new Date(locationModal.location.shared_at).toLocaleString()}
                </div>
                <a
                  href={`https://www.google.com/maps?q=${locationModal.location.lat},${locationModal.location.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-block', padding: '8px 14px', borderRadius: 8, background: '#0f766e', color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none', marginBottom: 8 }}
                >
                  Open in Google Maps
                </a>
              </>
            ) : (
              <div style={{ color: '#64748b', fontSize: 13 }}>No shared trip location for this vehicle.</div>
            )}
            <div style={{ marginTop: 16 }}>
              <button onClick={() => setLocationModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#0f172a', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function pagerBtn(disabled) {
  return {
    padding: '6px 12px',
    border: '1px solid #cbd5e1',
    background: disabled ? '#f1f5f9' : '#fff',
    color: disabled ? '#94a3b8' : '#0f172a',
    borderRadius: 8,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
  };
}

// Individual vehicle row — its own component so hooks run at top level (Rules of Hooks).
function VehicleRow({ v, editing, setEditing, saveEdit, updateMutLoading, deleteMut, moderationMut, onViewLocation }) {
  const badge = statusBadge(v);
  const modBadge = MODERATION_BADGE[v.moderation_status] || MODERATION_BADGE.draft;
  const isEditing = editing === v.id;
  const form = useEditingForm(isEditing, v);

  return (
    <React.Fragment>
      <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
        <td style={{ padding: '12px 14px', verticalAlign: 'top' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {Array.isArray(v.photos) && v.photos[0] ? (
              <img src={v.photos[0].split('?')[0]} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0' }}/>
            ) : (
              <div style={{ width: 54, height: 54, background: '#f1f5f9', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🚙</div>
            )}
            <div>
              <div style={{ fontWeight: 800, fontSize: 14.5 }}>
                {v.year ? `${v.year} ` : ''}{v.make || '—'} {v.model || '—'}
              </div>
              <div style={{ fontSize: 12.5, color: '#475569', marginTop: 2 }}>
                🏷 {String(v.license_plate || 'n/a').toUpperCase()}
                {v.category ? <> · 🗂 {v.category}</> : null}
                {v.provider_group ? <> · 🏭 {v.provider_group}</> : null}
                {v.seating_capacity ? <> · 👥 {v.seating_capacity}</> : null}
              </div>
              {v.vin && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2, fontFamily: 'ui-monospace, monospace' }}>VIN: {v.vin}</div>}
            </div>
          </div>
        </td>
        <td style={{ padding: '12px 14px', verticalAlign: 'top', fontSize: 13 }}>
          {v.owner ? (
            <>
              <div style={{ fontWeight: 700 }}>{v.owner.full_name || 'Unnamed user'}</div>
              <div style={{ color: '#475569' }}>{v.owner.email}</div>
              {v.owner.phone && <div style={{ color: '#64748b', fontSize: 12 }}>{v.owner.phone}</div>}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'ui-monospace, monospace' }}>id: {v.owner.id}</div>
            </>
          ) : <span style={{ color: '#64748b' }}>—</span>}
        </td>
        <td style={{ padding: '12px 14px', verticalAlign: 'top', fontSize: 13, color: '#475569' }}>
          {v.location_city || v.location_address || (v.location_lat ? `📍 ${Number(v.location_lat).toFixed(4)}, ${Number(v.location_lng).toFixed(4)}` : '—')}
          {v.location_country_code ? <><br/><span style={{ color: '#94a3b8' }}>{v.location_country_code}</span></> : null}
          {v.here_place_id ? <><br/><span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>HERE: {v.here_place_id.slice(0, 18)}…</span></> : null}
        </td>
        <td style={{ padding: '12px 14px', verticalAlign: 'top', fontSize: 13 }}>
          <div style={{ fontWeight: 800, color: '#0f766e' }}>
            {v.currency_code || '$'}{Number(v.price_per_day || 0).toFixed(2)}<span style={{ color: '#64748b', fontWeight: 500 }}>/day</span>
          </div>
          {v.price_per_hour ? <div style={{ color: '#475569' }}>+ ${Number(v.price_per_hour).toFixed(2)}/hr</div> : null}
          {v.price_per_km ? <div style={{ color: '#475569' }}>+ ${Number(v.price_per_km).toFixed(3)}/km</div> : null}
          {v.service_fee && <div style={{ fontSize: 12, color: '#64748b' }}>Fee: ${Number(v.service_fee).toFixed(2)}</div>}
        </td>
        <td style={{ padding: '12px 14px', verticalAlign: 'top' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: badge.bg, color: badge.color, fontWeight: 700, fontSize: 12 }}>
            <span>{badge.icon}</span><span>{badge.text}</span>
          </div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>
            Created {new Date(v.created_at).toLocaleString()}
          </div>
          <div style={{ fontSize: 12, marginTop: 6, color: v.review_status === 'approved' ? '#166534' : v.review_status === 'rejected' ? '#991b1b' : '#92400e' }}>
            Vehicle review: {v.review_status || 'pending'} · inspection {v.inspection_document_url ? 'uploaded' : 'missing'}
          </div>
        </td>
        <td style={{ padding: '12px 14px', verticalAlign: 'top' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: modBadge.bg, color: modBadge.color, fontWeight: 700, fontSize: 12 }}>
            <span>{modBadge.icon}</span><span>{modBadge.text}</span>
          </div>
          {v.moderation_reason && (v.moderation_status === 'rejected' || v.moderation_status === 'changes_requested') && (
            <div style={{ fontSize: 11.5, color: '#9a3412', marginTop: 6, maxWidth: 180 }}>{v.moderation_reason}</div>
          )}
          {v.moderation_status === 'submitted' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
              <button
                onClick={() => moderationMut.mutate({ id: v.id, action: 'approve' })}
                disabled={moderationMut.isLoading}
                style={{ padding: '4px 8px', fontSize: 11.5, fontWeight: 700, borderRadius: 6, border: 'none', color: '#fff', background: '#16a34a', cursor: 'pointer' }}
              >✓ Approve</button>
              <button
                onClick={() => {
                  const reason = window.prompt('What needs to change?');
                  if (reason && reason.trim()) moderationMut.mutate({ id: v.id, action: 'request-changes', reason: reason.trim() });
                }}
                disabled={moderationMut.isLoading}
                style={{ padding: '4px 8px', fontSize: 11.5, fontWeight: 700, borderRadius: 6, border: '1px solid #fdba74', color: '#9a3412', background: '#fff', cursor: 'pointer' }}
              >✏️ Changes</button>
              <button
                onClick={() => {
                  const reason = window.prompt('Rejection reason:');
                  if (reason && reason.trim()) moderationMut.mutate({ id: v.id, action: 'reject', reason: reason.trim() });
                }}
                disabled={moderationMut.isLoading}
                style={{ padding: '4px 8px', fontSize: 11.5, fontWeight: 700, borderRadius: 6, border: 'none', color: '#fff', background: '#dc2626', cursor: 'pointer' }}
              >✕ Reject</button>
            </div>
          )}
        </td>
        <td style={{ padding: '12px 14px', verticalAlign: 'top' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button
              onClick={() => setEditing(isEditing ? null : v.id)}
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #0f766e', color: '#0f766e', background: '#fff', cursor: 'pointer' }}
            >
              {isEditing ? 'Cancel' : '✏️ Edit'}
            </button>
            <button
              onClick={onViewLocation}
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #7c3aed', color: '#7c3aed', background: '#fff', cursor: 'pointer' }}
            >
              📍 Trip location
            </button>
            <button
              onClick={() => {
                const plate = v.license_plate || 'this vehicle';
                const ok = window.confirm(`Permanently delete ${v.make || ''} ${v.model || ''} (${plate})?\n\nIf it has active bookings or past records this will fail — hide it instead.`);
                if (!ok) return;
                deleteMut.mutate(v.id);
              }}
              disabled={deleteMut.isLoading}
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #fecaca', color: '#b91c1c', background: '#fff', cursor: 'pointer' }}
            >
              🗑 Delete
            </button>
          </div>
        </td>
      </tr>
      {isEditing && (
        <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
          <td colSpan={7} style={{ padding: 'clamp(8px, 2vw, 16px)' }}>
            {/* Pinned to the screen width while the table scrolls sideways on phones */}
            <div style={{ position: 'sticky', left: 0, maxWidth: 'calc(100vw - 32px)' }}>
            <EditVehicleForm
              v={v}
              form={form.current}
              onChange={form.set}
              onSave={() => saveEdit(v, form.current)}
              onCancel={() => setEditing(null)}
              saving={updateMutLoading}
            />
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

// Use a ref-like form state per edit row so edits don't cause whole-list re-renders.
// Since rows are independent, we memoize via component-local hook:
function useEditingForm(isEditing, v) {
  const initial = useMemo(() => (v ? flattenVehicle(v) : {}), [v]);
  const [form, setForm] = useState(initial);
  // Keep ref accessible so parent can save
  const ref = useMemoRef();
  ref.current = form;
  ref.set = (patch) => setForm((f) => ({ ...f, ...(typeof patch === 'function' ? patch(f) : patch) }));
  // Fresh values each time the editor opens (or another vehicle is opened) —
  // never while typing, so edits are kept until Save or Cancel.
  useEffect(() => {
    if (isEditing) setForm(initial);
  }, [isEditing, v?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
}

function flattenVehicle(v) {
  return {
    taxes_declared: v.taxes_declared || '',
    reason: '',
    make: v.make || '',
    model: v.model || '',
    year: v.year ?? '',
    license_plate: v.license_plate || '',
    vin: v.vin || '',
    color: v.color || '',
    currency_code: v.currency_code || '',
    photos: Array.isArray(v.photos) ? [...v.photos] : [],
    features: Array.isArray(v.features) ? [...v.features] : [],
    rules: Array.isArray(v.rules) ? [...v.rules] : [],
    category: v.category || 'sedan',
    provider_group: v.provider_group || 'p2p',
    transmission: v.transmission || 'automatic',
    fuel_type: v.fuel_type || 'gasoline',
    seating_capacity: v.seating_capacity ?? 4,
    comfort_score: v.comfort_score ?? 3,
    co2_grams_per_km: v.co2_grams_per_km ?? '',
    price_per_day: v.price_per_day ?? '',
    price_per_hour: v.price_per_hour ?? '',
    price_per_km: v.price_per_km ?? '',
    service_fee: v.service_fee ?? 0,
    minimum_rental_hours: v.minimum_rental_hours ?? 1,
    is_available: !!v.is_available,
    is_listed_publicly: !!v.is_listed_publicly,
    is_listed_in_search: v.is_listed_in_search !== false,
    fleet_approval_required: !!v.fleet_approval_required,
    booking_mode: v.booking_mode || 'instant',
    mileage_policy: v.mileage_policy || 'unlimited',
    free_km_per_day: v.free_km_per_day ?? '',
    extra_km_price: v.extra_km_price ?? '',
    max_km_per_booking: v.max_km_per_booking ?? '',
    location_address: v.location_address || '',
    location_city: v.location_city || '',
    location_country_code: v.location_country_code || '',
    location_postal_code: v.location_postal_code || '',
    last_known_fuel_percent: v.last_known_fuel_percent ?? '',
    last_known_odometer_km: v.last_known_odometer_km ?? '',
    description: v.description || '',
  };
}

function useMemoRef() {
  const ref = React.useRef({});
  return ref.current;
}

// Pricing check for the review (Kayd, 24.10.2026): the complete customer price
// from the central calculation (saved values), the NULA-MOVE booking fee and
// the owner's agreements — fees are edited only in Fees & Taxes.
function VehiclePricing({ vehicleId }) {
  const [p, setP] = useState(null);
  const [central, setCentral] = useState(null);
  useEffect(() => {
    client.get(`/api/fees/listings/vehicle/${vehicleId}/preview`, { params: { units: 3 } }).then((r) => setP(r.data?.preview)).catch(() => setP(null));
    client.get(`/api/fees/listings/vehicle/${vehicleId}/central-fees`).then((r) => setCentral(r.data)).catch(() => setCentral(null));
  }, [vehicleId]);
  if (!p) return null;
  const m = (c) => `${p.currency} ${((Number(c) || 0) / 100).toFixed(2)}`;
  return (
    <div style={{ marginTop: 12, fontSize: 12.5, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
      <strong>Pricing check — example for 3 rental days</strong>
      <div>Rental {m(p.base)}{p.fees ? ` · service fee ${m(p.fees)}` : ''}{p.taxes_online ? ` · taxes ${m(p.taxes_online)}` : ''}{p.taxes_included ? ` · included ${m(p.taxes_included)}` : ''} · NULA-MOVE booking fee {['applied', 'individual'].includes(p.platform_fee_status) ? m(p.platform_fee) : p.platform_fee_status === 'exempt' ? 'switched off' : '—'}</div>
      <div><strong>Total {m(p.total)}</strong> · pay online now {m(p.pay_online_now ?? p.online)} · pay on site {m(0)}</div>
      {p.allocation && <div>Split: NULA-MOVE {m(p.allocation.platform_share)} · provider {m(p.allocation.provider_share)}</div>}
      {(p.warnings || []).map((w, i) => <div key={i} style={{ color: '#92400e' }}>⚠️ {w.message}</div>)}
      {central?.agreements?.length > 0 && <div>Individual rules of this provider: {central.agreements.map((a) => `${a.agreement_number} (${a.fee_model === 'booking_fee' ? (a.treatment === 'exemption' ? 'booking fee switched off' : 'individual booking fee') : 'commission'})`).join(', ')}</div>}
    </div>
  );
}

function VehicleHistory({ vehicleId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { client.get(`/api/admin/vehicles/${vehicleId}/changes`).then((r) => setRows(r.data?.changes || [])).catch(() => setRows([])); }, [vehicleId]);
  if (!rows || !rows.length) return null;
  return (
    <div style={{ marginTop: 12, fontSize: 12.5, color: '#475569' }}>
      <strong>Change history</strong>
      {rows.slice(0, 10).map((h) => (
        <div key={h.id} style={{ borderTop: '1px solid #f1f5f9', padding: '6px 0' }}>
          {new Date(h.created_at).toLocaleString()} · {h.admin_name || 'Admin'}{h.reason ? ` · ${h.reason}` : ''}{h.provider_notified ? ' · owner notified' : ''}
          {Object.entries(h.changes || {}).map(([k, c]) => <div key={k}>{k.replace(/_/g, ' ')}: {JSON.stringify(c.from)} → {JSON.stringify(c.to)}</div>)}
        </div>
      ))}
    </div>
  );
}

function EditVehicleForm({ v, form, onChange, onSave, onCancel, saving }) {
  const currency = form.currency_code || 'USD';
  const currencySymbol = CURRENCY_SYMBOLS[currency] || currency;

  const inputStyle = {
    fontSize: 14, color: '#0f172a', background: '#f8fafc',
    border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 13, color: '#475569', fontWeight: 400, marginBottom: 4, display: 'block' };
  const hintStyle = { fontSize: 12, color: '#94a3b8', marginTop: 3 };
  const sectionStyle = {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: 'clamp(14px, 3vw, 20px) clamp(14px, 3vw, 24px)', marginBottom: 16,
  };
  const sectionTitleStyle = {
    fontSize: 13, fontWeight: 500, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: '.06em', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
  };
  // Columns wrap onto their own lines on narrow screens.
  const rowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 };
  const rowThreeStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 };
  const rowFullStyle = { display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 12 };
  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4 };

  const Chip = ({ active, onClick, children }) => (
    <span
      onClick={onClick}
      style={{
        padding: '5px 12px', borderRadius: 20, border: active ? '1px solid #0f766e' : '1px solid #cbd5e1',
        fontSize: 13, cursor: 'pointer', color: active ? '#0f766e' : '#475569',
        background: active ? '#f0fdfa' : '#f8fafc', fontWeight: active ? 500 : 400,
        transition: 'all .15s', userSelect: 'none',
      }}
    >{children}</span>
  );

  const ChipGroup = ({ value, options, onChange: onPick }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((o) => (
        <Chip key={o.v} active={value === o.v} onClick={() => onPick(o.v)}>{o.label}</Chip>
      ))}
    </div>
  );

  const Toggle = ({ checked, onChange: onToggle, label, desc }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 14, color: '#0f172a' }}>{label}</span>
        {desc && <span style={{ fontSize: 12, color: '#94a3b8' }}>{desc}</span>}
      </div>
      <label style={{ position: 'relative', width: 40, height: 22, flexShrink: 0 }}>
        <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
        <span style={{
          position: 'absolute', cursor: 'pointer', inset: 0,
          background: checked ? '#0f766e' : '#cbd5e1', borderRadius: 22, transition: '.2s',
        }}>
          <span style={{
            content: '', position: 'absolute', height: 16, width: 16, left: 3, bottom: 3,
            background: 'white', borderRadius: '50%', transition: '.2s',
            transform: checked ? 'translateX(18px)' : 'translateX(0)',
          }} />
        </span>
      </label>
    </div>
  );

  const PriceCard = ({ label, fieldKey, step }) => (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, color: '#475569' }}>{currencySymbol}</span>
        <input
          type="number" step={step || '0.01'} placeholder="—"
          value={form[fieldKey] ?? ''}
          onChange={(e) => onChange({ [fieldKey]: e.target.value })}
          style={{ fontSize: 15, fontWeight: 500, color: '#0f172a', background: 'transparent', border: 'none', outline: 'none', width: 80 }}
        />
      </div>
    </div>
  );

  const AvailCard = ({ selected, onClick, icon, title, desc }) => (
    <div
      onClick={onClick}
      style={{
        background: selected ? '#f0fdfa' : '#f8fafc',
        border: selected ? '1px solid #0f766e' : '1px solid #e2e8f0',
        borderRadius: 8, padding: '10px 12px', cursor: 'pointer', transition: 'all .15s',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 500, color: selected ? '#0f766e' : '#0f172a' }}>{icon} {title}</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{desc}</div>
    </div>
  );

  return (
    <div>
      {/* Vehicle Identity */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>🚗 Vehicle Identity</div>
        <div style={rowThreeStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Make</label>
            <input value={form.make ?? ''} onChange={(e) => onChange({ make: e.target.value })} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Model</label>
            <input value={form.model ?? ''} onChange={(e) => onChange({ model: e.target.value })} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Year</label>
            <input type="number" value={form.year ?? ''} onChange={(e) => onChange({ year: e.target.value })} style={inputStyle} />
          </div>
        </div>
        <div style={rowThreeStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>License Plate</label>
            <input value={form.license_plate ?? ''} onChange={(e) => onChange({ license_plate: e.target.value })} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>VIN (optional)</label>
            <input value={form.vin ?? ''} placeholder="WBA…" onChange={(e) => onChange({ vin: e.target.value })} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Color</label>
            <input value={form.color ?? ''} placeholder="e.g. Pearl White" onChange={(e) => onChange({ color: e.target.value })} style={inputStyle} />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Mileage (km)</label>
            <input type="number" value={form.last_known_odometer_km ?? ''} onChange={(e) => onChange({ last_known_odometer_km: e.target.value })} style={inputStyle} />
            <span style={hintStyle}>Updated automatically after every ride</span>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>CO₂ g/km</label>
            <input type="number" value={form.co2_grams_per_km ?? ''} onChange={(e) => onChange({ co2_grams_per_km: e.target.value })} style={inputStyle} />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>✨</span> <span>Calculated from vehicle type &amp; fuel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category & Provider Group */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>🗂 Category &amp; Provider Group</div>
        <div style={{ ...fieldStyle, marginBottom: 12 }}>
          <label style={labelStyle}>Vehicle Category</label>
          <ChipGroup
            value={form.category}
            onChange={(val) => onChange({ category: val })}
            options={[
              { v: 'economy', label: 'Economy' }, { v: 'sedan', label: 'Sedan' }, { v: 'suv', label: 'SUV' },
              { v: 'luxury', label: 'Luxury' }, { v: 'van', label: 'Van' }, { v: 'truck', label: 'Truck' },
              { v: 'scooter', label: 'Scooter' }, { v: 'bike', label: 'Bicycle' },
            ]}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Provider Group</label>
          <ChipGroup
            value={form.provider_group}
            onChange={(val) => onChange({ provider_group: val })}
            options={[
              { v: 'p2p', label: 'P2P · Owner-Listed' }, { v: 'carshare', label: 'CarShare Fleet' },
              { v: 'scooter', label: 'Scooter Fleet' }, { v: 'ride', label: 'Ride-Hail' }, { v: 'public_transit', label: 'Public Transit' },
            ]}
          />
        </div>
      </div>

      {/* Powertrain & Equipment */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>⚙️ Powertrain &amp; Equipment</div>
        <div style={{ ...fieldStyle, marginBottom: 12 }}>
          <label style={labelStyle}>Transmission</label>
          <ChipGroup
            value={form.transmission}
            onChange={(val) => onChange({ transmission: val })}
            options={[
              { v: 'automatic', label: 'Automatic' }, { v: 'manual', label: 'Manual' },
              { v: 'ev', label: 'Electric (EV)' }, { v: 'none', label: 'N/A' },
            ]}
          />
        </div>
        <div style={{ ...fieldStyle, marginBottom: 12 }}>
          <label style={labelStyle}>Fuel</label>
          <ChipGroup
            value={form.fuel_type}
            onChange={(val) => onChange({ fuel_type: val })}
            options={[
              { v: 'gasoline', label: 'Gasoline' }, { v: 'diesel', label: 'Diesel' },
              { v: 'hybrid', label: 'Hybrid' }, { v: 'electric', label: 'Electric' },
              { v: 'hydrogen', label: 'Hydrogen' }, { v: 'none', label: 'N/A' },
            ]}
          />
        </div>
        <div style={rowStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Seats</label>
            <input type="number" min="1" max="20" value={form.seating_capacity ?? ''} onChange={(e) => onChange({ seating_capacity: e.target.value })} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Comfort (0–5)</label>
            <input type="number" min="0" max="5" step="0.5" value={form.comfort_score ?? ''} onChange={(e) => onChange({ comfort_score: e.target.value })} style={inputStyle} />
          </div>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Fuel Level <span style={{ fontWeight: 500, color: '#0f172a' }}>{form.last_known_fuel_percent ?? 0}%</span></label>
          <input
            type="range" min="0" max="100" step="1"
            value={form.last_known_fuel_percent ?? 0}
            onChange={(e) => onChange({ last_known_fuel_percent: e.target.value })}
            style={{ marginTop: 6, width: '100%' }}
          />
          <div style={{ height: 8, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden', marginTop: 6 }}>
            <div style={{ height: '100%', borderRadius: 4, background: '#f59e0b', width: `${form.last_known_fuel_percent ?? 0}%`, transition: 'width .3s' }} />
          </div>
          <span style={hintStyle}>Condition before handover — saved in the handover log</span>
        </div>
      </div>

      {/* Pricing */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>💶 Pricing</div>
        <div style={{ ...fieldStyle, marginBottom: 12, maxWidth: 260 }}>
          <label style={labelStyle}>Currency</label>
          <select value={form.currency_code || 'USD'} onChange={(e) => onChange({ currency_code: e.target.value })} style={inputStyle}>
            {Object.entries(CURRENCY_LABELS).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 12 }}>
          <PriceCard label="Per Day" fieldKey="price_per_day" />
          <PriceCard label="Per Hour (opt.)" fieldKey="price_per_hour" />
          <PriceCard label="Per km (opt.)" fieldKey="price_per_km" step="0.001" />
        </div>
        <div style={rowStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Service Fee %</label>
            <input type="number" value={form.service_fee ?? ''} onChange={(e) => onChange({ service_fee: e.target.value })} style={inputStyle} />
            <span style={hintStyle}>Nula Move platform fee</span>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Minimum Rental Duration</label>
            <select
              value={form.minimum_rental_hours ?? 1}
              onChange={(e) => onChange({ minimum_rental_hours: e.target.value })}
              style={inputStyle}
            >
              <option value="0.5">30 minutes</option>
              <option value="1">1 hour</option>
              <option value="2">2 hours</option>
              <option value="4">4 hours</option>
              <option value="24">1 day</option>
              <option value="72">3 days</option>
            </select>
          </div>
        </div>

        {/* Free Mileage Policy */}
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '12px 14px', marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 16 }}>🛣️</span>
            <span style={{ fontSize: 13, color: '#065f46', fontWeight: 500 }}>Free Mileage Policy</span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button
              onClick={() => onChange({ mileage_policy: 'unlimited' })}
              style={{
                padding: '5px 14px', borderRadius: 20, border: form.mileage_policy === 'unlimited' ? '1px solid #a7f3d0' : '1px solid #cbd5e1',
                fontSize: 13, cursor: 'pointer', background: form.mileage_policy === 'unlimited' ? '#ecfdf5' : '#f8fafc',
                color: form.mileage_policy === 'unlimited' ? '#065f46' : '#475569', fontWeight: form.mileage_policy === 'unlimited' ? 500 : 400,
              }}
            >Unlimited</button>
            <button
              onClick={() => onChange({ mileage_policy: 'limited' })}
              style={{
                padding: '5px 14px', borderRadius: 20, border: form.mileage_policy === 'limited' ? '1px solid #a7f3d0' : '1px solid #cbd5e1',
                fontSize: 13, cursor: 'pointer', background: form.mileage_policy === 'limited' ? '#ecfdf5' : '#f8fafc',
                color: form.mileage_policy === 'limited' ? '#065f46' : '#475569', fontWeight: form.mileage_policy === 'limited' ? 500 : 400,
              }}
            >Limited</button>
          </div>
          {form.mileage_policy === 'limited' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 13, color: '#475569', minWidth: 120 }}>Free km / Day</label>
                <input type="number" placeholder="e.g. 200" value={form.free_km_per_day ?? ''} onChange={(e) => onChange({ free_km_per_day: e.target.value })} style={{ ...inputStyle, width: 90, background: '#fff' }} />
                <span style={{ fontSize: 13, color: '#475569' }}>km</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 13, color: '#475569', minWidth: 120 }}>Extra km Price</label>
                <span style={{ fontSize: 13, color: '#475569' }}>{currencySymbol}</span>
                <input type="number" step="0.01" placeholder="0.25" value={form.extra_km_price ?? ''} onChange={(e) => onChange({ extra_km_price: e.target.value })} style={{ ...inputStyle, width: 90, background: '#fff' }} />
                <span style={{ fontSize: 13, color: '#475569' }}>/ km</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 13, color: '#475569', minWidth: 120 }}>Max. km / Booking</label>
                <input type="number" placeholder="opt." value={form.max_km_per_booking ?? ''} onChange={(e) => onChange({ max_km_per_booking: e.target.value })} style={{ ...inputStyle, width: 90, background: '#fff' }} />
                <span style={{ fontSize: 13, color: '#475569' }}>km <span style={{ fontSize: 11, color: '#94a3b8' }}>(opt.)</span></span>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#065f46' }}>
              <span>∞</span> No mileage limit — priced by time only
            </div>
          )}
        </div>
      </div>

      {/* Location */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>📍 Location</div>
        <div style={rowFullStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Street Address</label>
            <input value={form.location_address ?? ''} onChange={(e) => onChange({ location_address: e.target.value })} style={inputStyle} />
          </div>
        </div>
        <div style={rowThreeStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>City</label>
            <input value={form.location_city ?? ''} onChange={(e) => onChange({ location_city: e.target.value })} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Postal Code</label>
            <input value={form.location_postal_code ?? ''} onChange={(e) => onChange({ location_postal_code: e.target.value })} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Country Code</label>
            <select value={form.location_country_code ?? 'US'} onChange={(e) => onChange({ location_country_code: e.target.value })} style={inputStyle}>
              <option value="US">US</option>
              <option value="DE">DE</option>
              <option value="AT">AT</option>
              <option value="CH">CH</option>
              <option value="KE">KE</option>
              <option value="GB">GB</option>
              <option value="FR">FR</option>
            </select>
          </div>
        </div>
      </div>

      {/* Operation & Visibility */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>🔘 Operation &amp; Visibility</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>Booking Mode</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <AvailCard
              selected={form.booking_mode === 'instant'}
              onClick={() => onChange({ booking_mode: 'instant' })}
              icon="⚡"
              title="Instant Booking"
              desc="No confirmation — guest books directly"
            />
            <AvailCard
              selected={form.booking_mode === 'request'}
              onClick={() => onChange({ booking_mode: 'request' })}
              icon="🕐"
              title="By Request"
              desc="Operator approves every booking manually"
            />
          </div>
        </div>
        <Toggle
          checked={!!form.is_available}
          onChange={(val) => onChange({ is_available: val })}
          label="Vehicle Available"
          desc="Can receive bookings"
        />
        <Toggle
          checked={!!form.is_listed_publicly}
          onChange={(val) => onChange({ is_listed_publicly: val })}
          label="Visible on Public Map"
          desc="Appears for drivers in search"
        />
        <Toggle
          checked={!!form.is_listed_in_search}
          onChange={(val) => onChange({ is_listed_in_search: val })}
          label="Listed in Search Results"
          desc="Appears in vehicle list &amp; filter results"
        />
        <Toggle
          checked={!!form.fleet_approval_required}
          onChange={(val) => onChange({ fleet_approval_required: val })}
          label="Fleet Approval Required"
          desc="Fleet operators only — approve manually"
        />
      </div>

      {/* Photos, features & rules — the same fields as the owner's form */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>📸 Photos ({(form.photos || []).length}/{MAX_VEHICLE_PHOTOS})</div>
        <VehiclePhotosEditor photos={form.photos || []} ownerId={v.owner_id} onChange={(photos) => onChange({ photos })} />
      </div>
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>⭐ Features &amp; rules</div>
        <label style={labelStyle}>Features</label>
        <ListChips options={VEHICLE_FEATURES} value={form.features || []} onChange={(features) => onChange({ features })} placeholder="Add another feature" />
        <label style={{ ...labelStyle, marginTop: 14 }}>Rental / usage rules</label>
        <ListChips options={VEHICLE_RULES} value={form.rules || []} onChange={(rules) => onChange({ rules })} placeholder="Add another rule" />
      </div>

      {/* Description & Internal Notes */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>📝 Description &amp; Internal Notes</div>
        <textarea
          value={form.description ?? ''}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="e.g. special features, notes for drivers, internal notes …"
          style={{ ...inputStyle, resize: 'vertical', minHeight: 72, fontFamily: 'inherit' }}
        />
      </div>

      {/* Taxes, fees, reason and history (Kayd, 24.10.2026) */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>🧾 Taxes &amp; local charges · change log</div>
        <label style={labelStyle}>Taxes &amp; local charges</label>
        <select value={form.taxes_declared || ''} onChange={(e) => onChange({ taxes_declared: e.target.value })} style={{ ...inputStyle, maxWidth: 360 }}>
          <option value="">Not confirmed yet</option>
          <option value="none">No additional local taxes or levies apply</option>
          <option value="some">Local taxes or levies apply</option>
        </select>
        <div style={hintStyle}>The vehicle's individual taxes and charges are managed in <a href="/fees?tab=b">Fees &amp; Taxes → tab B</a>. The NULA-MOVE booking fee is set centrally there (tab A).</div>
        <label style={{ ...labelStyle, marginTop: 12 }}>Reason for this change (optional — logged; the owner is notified about price changes)</label>
        <input value={form.reason || ''} onChange={(e) => onChange({ reason: e.target.value })} style={inputStyle} />
        <VehiclePricing vehicleId={v.id} />
        <VehicleHistory vehicleId={v.id} />
      </div>

      {/* Save Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
        background: '#fff', border: '1px solid #0f766e', borderRadius: 12,
        padding: 'clamp(12px, 3vw, 16px) clamp(14px, 3vw, 24px)', marginTop: 16,
      }}>
        <span style={{ fontSize: 13, color: '#475569', minWidth: 0, overflowWrap: 'anywhere' }}>
          ✅ {form.make} {form.model} · {form.license_plate} · {form.location_city}
        </span>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button
            onClick={onCancel}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              background: 'transparent', color: '#475569', border: '1px solid #cbd5e1',
              borderRadius: 8, fontSize: 13, cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            disabled={saving}
            onClick={onSave}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px',
              background: '#0f766e', color: 'white', border: 'none',
              borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? '⏳ Saving…' : '💾 Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Photos, features and rules (Kayd, 24.10.2026: admins edit every field the
// owner can). Same values and limits as the owner's vehicle form.
const MAX_VEHICLE_PHOTOS = 30;
const CURRENCY_SYMBOLS = { USD: '$', KES: 'KSh', PKR: 'Rs', GBP: '£', EUR: '€', AED: 'د.إ' };
const CURRENCY_LABELS = { USD: 'USD ($)', KES: 'KES (KSh)', PKR: 'PKR (Rs)', GBP: 'GBP (£)', EUR: 'EUR (€)', AED: 'AED (د.إ)' };
const VEHICLE_FEATURES = [
  'Bluetooth', 'GPS Navigation', 'Apple CarPlay', 'Android Auto', 'USB Charger',
  'Heated Seats', 'Leather Seats', 'Backup Camera', 'Parking Sensors', 'Sunroof',
  'Snow Tires', 'Roof Rack', 'Pet Friendly', 'Smoke Free',
];
const VEHICLE_RULES = [
  'No smoking', 'No pets', 'No off-road driving', 'Return with full tank',
  'No food/drinks inside', 'Speed limit respected',
];

// The first photo is the cover shown to customers.
function VehiclePhotosEditor({ photos, ownerId, onChange }) {
  const [uploading, setUploading] = useState(false);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= photos.length) return;
    const list = [...photos];
    [list[i], list[j]] = [list[j], list[i]];
    onChange(list);
  };
  const makeCover = (i) => onChange([photos[i], ...photos.filter((_, j) => j !== i)]);
  const remove = (i) => {
    if (!window.confirm('Remove this photo from the vehicle? It is removed when you save.')) return;
    onChange(photos.filter((_, j) => j !== i));
  };
  const upload = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, Math.max(0, MAX_VEHICLE_PHOTOS - photos.length));
    if (!files.length) return;
    setUploading(true);
    const added = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('bucket', 'vehicle-photos');
        fd.append('folder', `${ownerId || 'admin'}/${Date.now()}`);
        const { data } = await client.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) added.push(data.url);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Photo upload failed');
    } finally {
      if (added.length) onChange([...photos, ...added]);
      setUploading(false);
    }
  };
  const btn = { minWidth: 32, height: 30, padding: '0 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: 13, cursor: 'pointer' };
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(140px, 100%), 1fr))', gap: 10 }}>
        {photos.map((url, i) => (
          <div key={`${url}-${i}`} style={{ position: 'relative', border: '1px solid #e2e8f0', borderRadius: 8, padding: 6, background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <img src={String(url).split('?')[0]} alt={`Vehicle ${i + 1} of ${photos.length}`} style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 6, display: 'block' }} />
            {i === 0 && <span style={{ position: 'absolute', top: 10, left: 10, background: '#0f766e', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>Cover</span>}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <button type="button" style={btn} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move left">←</button>
              <button type="button" style={btn} onClick={() => move(i, 1)} disabled={i === photos.length - 1} aria-label="Move right">→</button>
              {i > 0 && <button type="button" style={btn} onClick={() => makeCover(i)} title="Make cover photo" aria-label="Make cover photo">★</button>}
              <button type="button" style={{ ...btn, color: '#b91c1c', borderColor: '#fecaca', marginLeft: 'auto' }} onClick={() => remove(i)} aria-label="Remove photo">✕</button>
            </div>
          </div>
        ))}
        {photos.length < MAX_VEHICLE_PHOTOS && (
          <label style={{ border: '2px dashed #94a3b8', borderRadius: 8, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 10, color: '#475569', fontSize: 13, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', background: '#fff' }}>
            {uploading ? 'Uploading…' : '+ Add photos'}
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} disabled={uploading} onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
          </label>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>Use ← → to change the order. The first photo is the cover. Changes are saved with “Save”.</div>
    </div>
  );
}

// Toggle the standard options and add / remove custom entries.
function ListChips({ options, value, onChange, placeholder }) {
  const [custom, setCustom] = useState('');
  const toggle = (x) => onChange(value.includes(x) ? value.filter((y) => y !== x) : [...value, x]);
  const add = () => {
    const t = custom.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setCustom('');
  };
  const chip = (on) => ({
    padding: '5px 12px', borderRadius: 20, border: on ? '1px solid #0f766e' : '1px solid #cbd5e1',
    fontSize: 13, cursor: 'pointer', color: on ? '#0f766e' : '#475569', background: on ? '#f0fdfa' : '#f8fafc', fontWeight: on ? 500 : 400,
  });
  const extra = value.filter((x) => !options.includes(x));
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => <button type="button" key={o} style={chip(value.includes(o))} onClick={() => toggle(o)} aria-pressed={value.includes(o)}>{o}</button>)}
        {extra.map((o) => <button type="button" key={o} style={chip(true)} onClick={() => toggle(o)} title="Remove">{o} ✕</button>)}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={placeholder}
          style={{ flex: '1 1 180px', minWidth: 0, fontSize: 14, color: '#0f172a', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }} />
        <button type="button" onClick={add} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #0f766e', background: '#fff', color: '#0f766e', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add</button>
      </div>
    </div>
  );
}
